import is from '@sindresorhus/is';
import CryptoJS from 'crypto-js';
import Maybe from 'true-myth/maybe';
import Result from 'true-myth/result';
import { z } from 'zod';

import { createCognitoState } from './cognito-state';
import { sessionResponseSchema, sessionTransferResponseSchema } from './session';

import type { AuthorizationProvider } from './authorization-provider';
import type { CognitoStateSession } from './cognito-state';
import type { SessionTransferResponse } from './session';
import type { SessionResponse } from './session';
import type { Clock } from '../clock/clock';
import type { OidcConfiguration, OidcProvider } from '../customization/customization-response';
import type { Logger } from '../logger/logger';
import type { ValidatingHttpFetcher } from '../validating-http-fetcher/validating-http-fetcher';

const refreshTokenStorageKey = 'rt';
const refreshTokenSoftExpiryStorageKey = 'rte';
const refreshTokenSoftExpiryInMillis = 39_600_000;
const providerIdStorageKey = 'pid';
const urlSchema = z.url();

export const selfServicePoolUrl = 'https://cb-self-service-prod.auth.eu-central-1.amazoncognito.com';
export const selfServiceLogin = 'https://signin.cobrainer.com/signin';

export type LocationApi = Pick<Location, 'href' | 'origin' | 'search'> & {
    readonly assign: (url: string) => ReturnType<Location['assign']>;
    readonly replace: (url: string) => ReturnType<Location['replace']>;
};

export type CustomStorage = {
    readonly clear: () => void;
    readonly getItem: (key: string) => Promise<string | null> | string | null;
    readonly removeItem: (key: string) => void;
    readonly setItem: (key: string, value: string) => void;
};

export type CognitoAuthProviderDependencies = {
    readonly apiBaseUrl: URL;
    readonly clock: Clock;
    readonly createUuid: () => string;
    readonly history?: History;
    readonly location: LocationApi;
    readonly logger: Logger;
    readonly storage: CustomStorage;
    readonly validatingHttpFetcher: ValidatingHttpFetcher;
};

type AuthorizationRequestBody = {
    readonly client_id: string;
    readonly code: string;
    readonly grant_type: 'authorization_code';
    readonly redirect_uri: string;
};

type RefreshTokenRequestBody = {
    readonly client_id: string;
    readonly grant_type: 'refresh_token';
    readonly refresh_token: string;
};

export type InitiateSessionFromCode = {
    (code: string): Promise<Result<unknown, Error>>;
};

export type InitiateSessionFromSessionId = {
    (nonce: string, sessionId: string): Promise<Result<unknown, Error>>;
};

export type AuthorizationReceived = (jwtToken: string) => void;

export type CognitoAuthProviderOptions = {
    readonly oidcConfiguration: OidcConfiguration;
    readonly onAuthorizationReceived: AuthorizationReceived;
    readonly onLoginStateChanged?: (isLoggedIn: boolean) => void;
};

export const createCognitoAuthorizationProvider = async (
    dependencies: CognitoAuthProviderDependencies,
    options: CognitoAuthProviderOptions,
): Promise<AuthorizationProvider> => {
    const { clock, createUuid, history, location, logger, storage, validatingHttpFetcher } = dependencies;
    const { oidcConfiguration, onAuthorizationReceived, onLoginStateChanged } = options;
    const cognitoState = createCognitoState();

    const fetchAuthToken = (
        baseUrl: URL,
        payload: AuthorizationRequestBody | RefreshTokenRequestBody,
    ): Promise<Result<SessionResponse, Error>> =>
        validatingHttpFetcher.post(sessionResponseSchema, {
            requestOptions: {
                body: new URLSearchParams(payload).toString(),
                headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                timeout: 20_000,
            },
            url: new URL('/oauth2/token', baseUrl),
        });

    const fetchSessionTransfer = (sessionId: string): Promise<Result<SessionTransferResponse, Error>> =>
        validatingHttpFetcher.get(sessionTransferResponseSchema, {
            requestOptions: {
                searchParams: {
                    sessionId,
                },
            },
            url: new URL('/users/me/session-transfer', dependencies.apiBaseUrl),
        });

    const cancelSession = (): void => {
        cognitoState.update({
            session: Maybe.nothing(),
        });
        onLoginStateChanged?.(cognitoState.get('session').isJust);
        storage.removeItem(refreshTokenStorageKey);
        storage.removeItem(refreshTokenSoftExpiryStorageKey);
        storage.removeItem(providerIdStorageKey);
    };

    const logout = (): void => {
        const session = cognitoState.get('session');

        if (session.isNothing) {
            return;
        }
        cancelSession();

        const oidcProvider: Maybe<OidcProvider> = cognitoState.get('oidcProvider');

        if (oidcProvider.isJust) {
            if (oidcProvider.value.loginUrl.toString().startsWith(selfServicePoolUrl)) {
                location.assign(selfServiceLogin);
            } else {
                const baseUrl = oidcProvider.value.loginUrl;
                const clientId = baseUrl.searchParams.get('client_id');

                const redirectAfterLogoutUrl = location.origin;

                const redirectUrl = new URL('/logout', baseUrl);
                redirectUrl.searchParams.set('client_id', clientId ?? '');
                redirectUrl.searchParams.set('logout_uri', redirectAfterLogoutUrl);

                location.assign(redirectUrl.href);
            }
        }
    };

    const updateSession = (sessionData: SessionResponse): void => {
        const { attributes, expiresInSeconds, jsonWebToken, refreshJsonWebToken } = sessionData;

        cognitoState.update({
            session: Maybe.of<CognitoStateSession>({
                attributes,
                expiresInSeconds,
                jsonWebToken,
            }),
        });

        const callback = cognitoState.get('callback');

        if (callback.isJust) {
            callback.value(jsonWebToken);
        }

        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        rescheduleSessionRefresh();

        if (is.nonEmptyString(refreshJsonWebToken)) {
            storage.setItem(refreshTokenStorageKey, refreshJsonWebToken);
            storage.setItem(
                refreshTokenSoftExpiryStorageKey,
                new Date(clock.getCurrentTimestamp() + refreshTokenSoftExpiryInMillis).toISOString(),
            );
        }

        onLoginStateChanged?.(cognitoState.get('session').isJust);
    };

    const refreshSessionWithToken = async (token: string): Promise<Result<unknown, Error>> => {
        const oidcProvider: Maybe<OidcProvider> = cognitoState.get('oidcProvider');

        if (oidcProvider.isJust) {
            const baseUrl = oidcProvider.value.loginUrl;
            const clientId = baseUrl.searchParams.get('client_id');

            if (is.nonEmptyString(clientId)) {
                const authToken = await fetchAuthToken(baseUrl, {
                    client_id: clientId,
                    grant_type: 'refresh_token',
                    refresh_token: token,
                });

                if (authToken.isErr) {
                    return Result.err(authToken.error);
                }

                updateSession(authToken.value);
            }

            return Result.ok();
        }

        return Result.err(new Error('Could not refresh auth token'));
    };

    const refreshSession = async (): Promise<void> => {
        const token = await storage.getItem(refreshTokenStorageKey);

        if (is.nonEmptyString(token)) {
            const refreshResult = await refreshSessionWithToken(token);

            if (refreshResult.isErr) {
                logout();
            }
        } else {
            logout();
        }
    };

    const rescheduleSessionRefresh = (): void => {
        const session = cognitoState.get('session');

        if (session.isNothing) {
            return;
        }

        const refreshTimeout = cognitoState.get('refreshTimeout');

        if (refreshTimeout.isJust) {
            clearTimeout(refreshTimeout.value);
        }

        if (session.isJust) {
            cognitoState.update({
                refreshTimeout: Maybe.of(
                    clock.setTimeout(refreshSession, (session.value.expiresInSeconds - 60) * 1000),
                ),
            });
        }
    };

    const initiateSessionFromCode: InitiateSessionFromCode = async (code) => {
        const oidcProvider = cognitoState.get('oidcProvider');

        if (oidcProvider.isJust) {
            const baseUrl = oidcProvider.value.loginUrl;
            const clientId = baseUrl.searchParams.get('client_id');

            if (is.nonEmptyString(clientId)) {
                const authToken = await fetchAuthToken(baseUrl, {
                    client_id: clientId,
                    code,
                    grant_type: 'authorization_code',
                    redirect_uri: location.origin,
                });

                if (authToken.isOk) {
                    updateSession(authToken.value);
                }

                return Result.ok();
            }
        }

        return Result.err(new Error('Could not initiate session'));
    };

    const initiateSessionFromSessionId: InitiateSessionFromSessionId = async (nonce, sessionId) => {
        const oidcProvider = cognitoState.get('oidcProvider');

        if (oidcProvider.isJust) {
            const baseUrl = oidcProvider.value.loginUrl;
            const clientId = baseUrl.searchParams.get('client_id');

            const userSession = await fetchSessionTransfer(sessionId);
            if (is.nonEmptyString(clientId) && userSession.isOk) {
                const refreshToken = CryptoJS.AES.decrypt(userSession.value.encrypted, nonce).toString(
                    CryptoJS.enc.Utf8,
                );

                const authToken = await fetchAuthToken(baseUrl, {
                    client_id: clientId,
                    grant_type: 'refresh_token',
                    refresh_token: refreshToken,
                });

                if (authToken.isOk) {
                    updateSession({
                        ...authToken.value,
                        refreshJsonWebToken: refreshToken,
                    });

                    return Result.ok();
                }
            }
        }

        return Result.err(new Error('Could not initiate session'));
    };

    const findIdpInitiatedProvider = (oidcConfiguration: OidcConfiguration): Maybe<OidcProvider> =>
        Maybe.of(
            oidcConfiguration.providers.find((provider) => provider.loginUrl.searchParams.has('idp_initiated_allowed')),
        );

    const findSelfServiceProvider = (oidcConfiguration: OidcConfiguration): Maybe<OidcProvider> =>
        Maybe.of(
            oidcConfiguration.providers.find((provider) => provider.loginUrl.toString().startsWith(selfServicePoolUrl)),
        );

    const findProviderById = (oidcConfiguration: OidcConfiguration, providerId: number): Maybe<OidcProvider> =>
        Maybe.of(oidcConfiguration.providers[providerId] ?? oidcConfiguration.providers[0]);

    const determineOidcProvider = ({
        idpInitiated,
        oidcConfiguration,
        oidcProviderId,
        selfServiceInitiated,
    }: {
        readonly idpInitiated: boolean;
        readonly oidcConfiguration: OidcConfiguration;
        readonly oidcProviderId: number;
        readonly selfServiceInitiated: boolean;
    }): Maybe<OidcProvider> => {
        const provider = idpInitiated
            ? findIdpInitiatedProvider(oidcConfiguration)
            : selfServiceInitiated
              ? findSelfServiceProvider(oidcConfiguration)
              : findProviderById(oidcConfiguration, oidcProviderId);

        if (provider.isJust) {
            const updatedProviderId = oidcConfiguration.providers.indexOf(provider.value);
            storage.setItem(providerIdStorageKey, updatedProviderId.toString());
        } else {
            storage.removeItem(providerIdStorageKey);
        }

        return provider;
    };

    const redirectToCallbackUri = (callbackUri: string | null): void => {
        if (urlSchema.safeParse(callbackUri).success) {
            history?.replaceState({}, document.title, callbackUri);
        }
    };

    const initWithStateAndCode = async (code: string, state: string): Promise<void> => {
        const callbackUri = await storage.getItem('callback_uri');
        storage.removeItem('callback_uri');

        const callbackState = await storage.getItem('callback_state');
        storage.removeItem('callback_state');

        if (state === callbackState || state === 'IDP_INITIATED') {
            const initiateResult = await initiateSessionFromCode(code);
            if (initiateResult.isOk) {
                redirectToCallbackUri(callbackUri);
            } else {
                logger.error(initiateResult.error);
            }
        } else {
            logger.warn("State parameter doesn't have expected value.");
        }
    };

    const initWithSessionId = async (nonce: string, sessionId: string): Promise<void> => {
        const callbackUri = await storage.getItem('callback_uri');
        storage.removeItem('callback_uri');

        const initiateResult = await initiateSessionFromSessionId(nonce, sessionId);
        if (initiateResult.isOk) {
            redirectToCallbackUri(callbackUri);
        } else {
            logger.error(initiateResult.error);
        }
    };

    const initWithRefreshToken = async (): Promise<void> => {
        const refreshToken = await storage.getItem(refreshTokenStorageKey);
        const refreshTokenSoftExpiry = await storage.getItem(refreshTokenSoftExpiryStorageKey);

        if (is.nonEmptyString(refreshToken)) {
            if (
                is.nonEmptyString(refreshTokenSoftExpiry) &&
                Date.parse(refreshTokenSoftExpiry) < clock.getCurrentTimestamp()
            ) {
                return;
            }
            const refreshResult = await refreshSessionWithToken(refreshToken);
            if (refreshResult.isErr) {
                logger.error(refreshResult.error);
                storage.removeItem(refreshTokenStorageKey);
                storage.removeItem(refreshTokenSoftExpiryStorageKey);
            }
        }
    };

    const initialize = async (
        oidcConfiguration: OidcConfiguration,
        authorizationReceived: AuthorizationReceived,
    ): Promise<void> => {
        const query = new URLSearchParams(location.search);
        const code = query.get('code');
        const state = query.get('state');
        const nonce = query.get('nonce');
        const sessionId = query.get('sessionId');
        const oidcProviderId = await storage.getItem(providerIdStorageKey);
        const parsedOidcProviderId = is.null(oidcProviderId) === false ? Number.parseInt(oidcProviderId) : 0;
        const oidcProvider: Maybe<OidcProvider> = determineOidcProvider({
            idpInitiated: state === 'IDP_INITIATED',
            oidcConfiguration,
            oidcProviderId: parsedOidcProviderId,
            selfServiceInitiated: is.string(nonce) && is.string(sessionId),
        });

        cognitoState.update({
            callback: Maybe.of(authorizationReceived),
            oidcProvider,
        });

        if (is.string(code) && is.string(state)) {
            await initWithStateAndCode(code, state);
        } else if (is.string(nonce) && is.string(sessionId)) {
            await initWithSessionId(nonce, sessionId);
        } else {
            await initWithRefreshToken();
        }
    };

    await initialize(oidcConfiguration, onAuthorizationReceived);

    return {
        get hideLogoutButtons() {
            const oidcProvider: Maybe<OidcProvider> = cognitoState.get('oidcProvider');

            return oidcProvider.mapOr(false, ({ hideLogoutButtons }) => hideLogoutButtons);
        },
        get isBlocked() {
            const attributes = cognitoState.get('session').get('attributes');

            return attributes.mapOr(false, (attributes) => is.emptyArray(attributes.roles) || attributes.isService);
        },
        get isLoggedIn() {
            return cognitoState.get('session').isJust;
        },
        login(oidcProviderId: number) {
            const oidcProvider: Maybe<OidcProvider> = determineOidcProvider({
                idpInitiated: false,
                oidcConfiguration,
                oidcProviderId,
                selfServiceInitiated: false,
            });

            if (oidcProvider.isJust) {
                const loginUrl = oidcProvider.value.loginUrl.href;

                if (loginUrl.startsWith(selfServicePoolUrl)) {
                    location.replace(selfServiceLogin);
                } else {
                    const randomState = createUuid();
                    storage.setItem('callback_uri', location.href);
                    storage.setItem('callback_state', randomState);

                    location.replace(`${loginUrl}&state=${randomState}&redirect_uri=${location.origin}`);
                }
            }
        },
        logout,
        get roles() {
            const session = cognitoState.get('session');

            return session.isJust ? session.value.attributes.roles : [];
        },
    };
};
